<template>
  <Container>
    <template #header>
      <HeaderFe />
    </template>
    <template #hero>
      <PageHero page="dokumen" title="Dokumen" current="Dokumen"
        subtitle="Arsip dokumen dan laporan yang tersedia." />
    </template>
    <template v-slot:default>
      <div class="min-h-screen pb-16 bg-gray-50/40">
        <!-- Toolbar: search + filter -->
        <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0 pt-8">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-center">
            <div class="relative w-full sm:max-w-sm">
              <div class="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-400 pointer-events-none">
                <MagnifyingGlassVue class="w-5 h-5" />
              </div>
              <input
                v-model="search"
                type="text"
                autocomplete="off"
                placeholder="Cari nama dokumen..."
                class="block w-full py-3 pl-12 pr-4 text-sm text-gray-900 bg-white border border-gray-200 shadow-sm rounded-xl placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition"
              />
            </div>
            <select
              v-model="selectedJenis"
              class="w-full py-3 pl-4 pr-8 text-sm text-gray-900 bg-white border border-gray-200 shadow-sm sm:w-56 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition"
            >
              <option value="">Semua jenis dokumen</option>
              <option v-for="opt in jenisOptions" :key="opt" :value="opt">
                {{ opt }}
              </option>
            </select>
          </div>
        </div>

        <!-- List -->
        <section class="mt-10">
          <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0">
            <div v-if="filteredDokumen.length > 0" class="space-y-4">
              <button
                v-for="(dokumen, index) in paginatedDokumen"
                :key="dokumen.id || index"
                type="button"
                @click="viewDokumen(dokumen.iddok)"
                class="flex items-start w-full gap-4 p-5 text-left transition-all duration-300 bg-white border border-gray-100 shadow-sm group rounded-2xl hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <div
                  class="flex items-center justify-center w-14 h-14 text-xl rounded-xl shrink-0 bg-primary/10 text-primary"
                >
                  <i class="fa-regular fa-file-lines"></i>
                </div>
                <div class="flex-1 min-w-0">
                  <h3
                    class="text-base font-semibold text-gray-900 transition-colors line-clamp-2 group-hover:text-primary"
                  >
                    {{ dokumen.nama_dok }}
                  </h3>
                  <div class="flex flex-wrap items-center gap-2 mt-1.5">
                    <span
                      v-if="dokumen.jenisdokumen?.nama"
                      class="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full text-primary bg-primary/10"
                    >
                      {{ dokumen.jenisdokumen.nama }}
                    </span>
                    <span
                      v-if="dokumen.created_at"
                      class="inline-flex items-center gap-1 text-xs text-gray-400"
                    >
                      <i class="fa-regular fa-calendar"></i>
                      {{ formatDate(dokumen.created_at) }}
                    </span>
                  </div>
                  <p
                    v-if="dokumen.deskripsi"
                    class="mt-1.5 text-sm text-gray-500 line-clamp-2"
                  >
                    {{ (dokumen.deskripsi || "").replace(/<[^>]*>/g, "") }}
                  </p>
                </div>
                <i
                  class="self-center hidden text-gray-300 transition-colors fa-solid fa-chevron-right sm:block group-hover:text-primary"
                ></i>
              </button>

              <!-- Pagination -->
              <nav
                v-if="totalPages > 1"
                class="flex items-center justify-center gap-1.5 pt-6"
              >
                <button
                  @click="goToPage(page - 1)"
                  :disabled="page === 1"
                  class="inline-flex items-center justify-center w-10 h-10 text-sm text-gray-600 transition-colors bg-white border border-gray-200 rounded-lg hover:border-primary hover:text-primary disabled:opacity-40 disabled:pointer-events-none"
                  aria-label="Sebelumnya"
                >
                  <i class="fa-solid fa-chevron-left"></i>
                </button>
                <button
                  v-for="p in totalPages"
                  :key="p"
                  @click="goToPage(p)"
                  :class="[
                    'inline-flex items-center justify-center w-10 h-10 text-sm font-medium rounded-lg border transition-colors',
                    p === page
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-primary hover:text-primary',
                  ]"
                >
                  {{ p }}
                </button>
                <button
                  @click="goToPage(page + 1)"
                  :disabled="page === totalPages"
                  class="inline-flex items-center justify-center w-10 h-10 text-sm text-gray-600 transition-colors bg-white border border-gray-200 rounded-lg hover:border-primary hover:text-primary disabled:opacity-40 disabled:pointer-events-none"
                  aria-label="Berikutnya"
                >
                  <i class="fa-solid fa-chevron-right"></i>
                </button>
              </nav>
            </div>

            <!-- Empty state -->
            <div
              v-else
              class="flex flex-col items-center justify-center px-4 py-20 text-center bg-white border border-gray-100 rounded-2xl"
            >
              <i
                class="mb-4 text-4xl text-gray-300 fa-regular fa-folder-open"
              ></i>
              <h3 class="mb-1 text-lg font-semibold text-gray-900">
                Dokumen tidak ditemukan
              </h3>
              <p class="max-w-sm text-sm text-gray-500">
                {{
                  search || selectedJenis
                    ? "Tidak ada dokumen yang sesuai dengan filter/pencarian."
                    : "Belum ada dokumen yang tersedia saat ini."
                }}
              </p>
            </div>
          </div>
        </section>

        <!-- Global loading overlay for fetching dokumen detail -->
        <Loading v-model:active="isLoading" :is-full-page="true" />

        <!-- Modal: View Dokumen Detail -->
        <ViewDokumen
          :isOpen="isModalOpen"
          :dokumendata="viewDokumenData"
          @update:isOpen="(v) => (isModalOpen = v)"
        />
      </div>
    </template>
    <template #footer>
      <FooterFe />
    </template>
  </Container>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import Container from "@/Layouts/Container.vue";
import HeaderFe from "@/Components/HeaderFe.vue";
import PageHero from "@/Components/Web/PageHero.vue";
import FooterFe from "@/Components/FooterFe.vue";
import MagnifyingGlassVue from "@/Components/MagnifyingGlass.vue";
import ViewDokumen from "@/Components/Web/ViewDokumen.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/css/index.css";

const props = defineProps({
  dokumen: {
    type: Array,
    required: true,
  },
});

const search = ref("");
const page = ref(1);
const perPage = 6;
const isModalOpen = ref(false);
const viewDokumenData = ref(null);
const isLoading = ref(false);
const selectedJenis = ref("");

// Build unique, sorted list of jenis dokumen names from data
const jenisOptions = computed(() => {
  const data = Array.isArray(props.dokumen)
    ? props.dokumen
    : props.dokumen?.data || [];
  const map = new Map();
  for (const d of data) {
    const raw = d?.jenisdokumen?.nama;
    if (!raw) continue;
    const norm = raw.trim();
    const key = norm.toLowerCase();
    if (!map.has(key)) map.set(key, norm);
  }
  return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
});

const filteredDokumen = computed(() => {
  const data = Array.isArray(props.dokumen)
    ? props.dokumen
    : props.dokumen?.data || [];

  const selected = (selectedJenis.value || "").trim().toLowerCase();
  let result = selected
    ? data.filter(
        (d) => (d?.jenisdokumen?.nama || "").trim().toLowerCase() === selected
      )
    : data;

  if (!search.value) return result;
  const q = search.value.toLowerCase();
  result = result.filter(
    (d) =>
      d.nama_dok?.toLowerCase().includes(q) ||
      (d.deskripsi && d.deskripsi.toLowerCase().includes(q)) ||
      (d.jenisdokumen?.nama && d.jenisdokumen.nama.toLowerCase().includes(q))
  );
  return result;
});

const paginatedDokumen = computed(() => {
  const start = (page.value - 1) * perPage;
  return filteredDokumen.value.slice(start, start + perPage);
});

const totalPages = computed(() =>
  Math.ceil(filteredDokumen.value.length / perPage)
);

function goToPage(p) {
  if (p >= 1 && p <= totalPages.value) {
    page.value = p;
  }
}

// Reset to first page when search or jenis filter changes
watch(search, () => {
  page.value = 1;
});
watch(selectedJenis, () => {
  page.value = 1;
});

const viewDokumen = async (id) => {
  isLoading.value = true;
  isModalOpen.value = true;
  viewDokumenData.value = null;
  try {
    const response = await fetch(`/dokumenkinerja/${id}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      throw new Error("Response is not JSON");
    }
    const data = await response.json();
    viewDokumenData.value = data.datadokumen;
  } catch (error) {
    console.error("Error fetching data:", error.message);
  } finally {
    isLoading.value = false;
  }
};

// Format date helper for list items
function formatDate(dateString) {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d)) return dateString;
  return d.toLocaleDateString("id-ID", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
</script>
